import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { SnapshotOverrideUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type SnapshotOverrideUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  relatedEntityType: string;
  relatedEntityTypeSelectedValue?: { displayId: string; id: string } | null;
  onRelatedEntityTypeChange?: (value: string) => void;
  relatedEntityTypeError?: string;
  relatedEntityId: string;
  relatedEntityIdSelectedValue?: { displayId: string; id: string } | null;
  onRelatedEntityIdChange?: (value: string) => void;
  relatedEntityIdError?: string;
  path: string;
  pathSelectedValue?: { displayId: string; id: string } | null;
  onPathChange?: (value: string) => void;
  pathError?: string;
  value: string;
  valueSelectedValue?: { displayId: string; id: string } | null;
  onValueChange?: (value: string) => void;
  valueError?: string;
  note: string;
  noteSelectedValue?: { displayId: string; id: string } | null;
  onNoteChange?: (value: string) => void;
  noteError?: string;
  active: boolean;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  activeError?: string;
  snapshotOverrideUpdateStore: SnapshotOverrideUpdateStore;
};

export const SnapshotOverrideUpdateForm: React.FC<SnapshotOverrideUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      relatedEntityType = "",
      relatedEntityTypeError,
      onRelatedEntityTypeChange = () => {},
      relatedEntityId = "",
      relatedEntityIdError,
      onRelatedEntityIdChange = () => {},
      path = "",
      pathError,
      onPathChange = () => {},
      value = "{}",
      valueError,
      onValueChange = () => {},
      note = "",
      noteError,
      onNoteChange = () => {},
      active = false,
      activeError,
      onActiveChange = () => {},
      snapshotOverrideUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="SnapshotOverride"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "relatedEntityType":
                    onRelatedEntityTypeChange?.(valor);
                    break;
                  case "relatedEntityId":
                    onRelatedEntityIdChange?.(valor);
                    break;
                  case "path":
                    onPathChange?.(valor);
                    break;
                  case "value":
                    onValueChange?.(valor);
                    break;
                  case "note":
                    onNoteChange?.(valor);
                    break;
                  case "active":
                    onActiveChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={snapshotOverrideUpdateStore.getCardErrors("null")}
              title={langStore.groupSnapshotOverrideLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={relatedEntityType}
                  error={Boolean(relatedEntityTypeError)}
                  warning={
                    !relatedEntityTypeError &&
                    Boolean(
                      snapshotOverrideUpdateStore.warnings["relatedEntityType"]
                    )
                  }
                  helperText={
                    relatedEntityTypeError ||
                    snapshotOverrideUpdateStore.warnings["relatedEntityType"]
                      ?.message
                  }
                  label={langStore.SnapshotOverrideRelatedEntityTypeLabel}
                  onChange={(value: string) => onRelatedEntityTypeChange(value)}
                  description={
                    langStore.SnapshotOverrideRelatedEntityTypeDescription
                  }
                />
                <InputString
                  value={relatedEntityId}
                  error={Boolean(relatedEntityIdError)}
                  warning={
                    !relatedEntityIdError &&
                    Boolean(
                      snapshotOverrideUpdateStore.warnings["relatedEntityId"]
                    )
                  }
                  helperText={
                    relatedEntityIdError ||
                    snapshotOverrideUpdateStore.warnings["relatedEntityId"]
                      ?.message
                  }
                  label={langStore.SnapshotOverrideRelatedEntityIdLabel}
                  onChange={(value: string) => onRelatedEntityIdChange(value)}
                  description={
                    langStore.SnapshotOverrideRelatedEntityIdDescription
                  }
                />
                <InputString
                  value={path}
                  error={Boolean(pathError)}
                  warning={
                    !pathError &&
                    Boolean(snapshotOverrideUpdateStore.warnings["path"])
                  }
                  helperText={
                    pathError ||
                    snapshotOverrideUpdateStore.warnings["path"]?.message
                  }
                  label={langStore.SnapshotOverridePathLabel}
                  onChange={(value: string) => onPathChange(value)}
                  description={langStore.SnapshotOverridePathDescription}
                />
                <InputString
                  value={value}
                  error={Boolean(valueError)}
                  warning={
                    !valueError &&
                    Boolean(snapshotOverrideUpdateStore.warnings["value"])
                  }
                  helperText={
                    valueError ||
                    snapshotOverrideUpdateStore.warnings["value"]?.message
                  }
                  label={langStore.SnapshotOverrideValueLabel}
                  onChange={(value: string) => onValueChange(value)}
                  description={langStore.SnapshotOverrideValueDescription}
                />
                <InputString
                  value={note}
                  error={Boolean(noteError)}
                  warning={
                    !noteError &&
                    Boolean(snapshotOverrideUpdateStore.warnings["note"])
                  }
                  helperText={
                    noteError ||
                    snapshotOverrideUpdateStore.warnings["note"]?.message
                  }
                  label={langStore.SnapshotOverrideNoteLabel}
                  onChange={(value: string) => onNoteChange(value)}
                  description={langStore.SnapshotOverrideNoteDescription}
                />
                <InputBoolean
                  value={active}
                  error={Boolean(activeError)}
                  helperText={
                    activeError ||
                    snapshotOverrideUpdateStore.warnings["active"]?.message
                  }
                  label={langStore.SnapshotOverrideActiveLabel}
                  description={langStore.SnapshotOverrideActiveDescription}
                  onChange={(value: boolean) => onActiveChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
